import { AddCircle } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDeleteShow } from '~/api/shows';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import { CreateViewWizard, useCreateViewWizard } from '../../components/create-view-wizard';
import type { ShowActions__Show } from './actions.generated';

/* GraphQL */ `#graphql
fragment ShowActions__Show on Show {
  id
  canDestroy {
    ...AuthorizationResult
  }
  canUpdate {
    ...AuthorizationResult
  }
  eventDeviceGroups {
    id
    deviceGroup {
      id
      name
      deviceCount
    }
    event {
      id
      name
      start
    }
    show {
      id
      name
    }
  }
  origin {
    id
  }
}
`;

export interface ActionsProps {
  show: ShowActions__Show;
}

export const Actions = ({ show }: ActionsProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const canManageViews = currentNetwork.canManageViews.value;
  const navigate = useNavigate();
  const link = useLink();
  const viewWizard = useCreateViewWizard();
  const [, setSearchParams] = useSearchParams();

  const [confirm, confirmProps] = useConfirmDialog();

  const [deleteShow] = useDeleteShow();

  const setViewId = useCallback(
    (viewId: number) => {
      setSearchParams(
        (prev) => {
          prev.set('viewId', String(viewId));
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleDelete = useCallback(async () => {
    if (!(await confirm())) return;
    await deleteShow({ variables: { showIds: [show.id] } });
    history.length > 1 ? navigate(-1) : navigate(link('/shows'));
  }, [confirm, deleteShow, link, navigate, show.id]);

  const { event, deviceGroup } = show.eventDeviceGroups.find((x) => x.show.id === show.id) || {};

  const prompt = useMemo(() => {
    if (!show.canDestroy.value) return null;
    if (show.origin && event && deviceGroup)
      return (
        <Trans
          i18nKey="pages.shows.detail.actions.delete_show_prompt_event"
          values={{
            eventName: event.name,
            eventStart: formatDate(event.start, { format: DateTime.DATETIME_MED }),
            deviceCount: deviceGroup.deviceCount,
            devicePlural: t('pages.shows.detail.actions.device_plural', {
              count: deviceGroup.deviceCount,
            }),
            deviceGroupName: deviceGroup.name,
          }}
          components={{ strong: <strong /> }}
        />
      );
    return t('pages.shows.detail.actions.delete_show_prompt');
  }, [deviceGroup, event, show, t]);

  if (!show.canUpdate.value) return <Box />;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {canManageViews && (
          <Button onClick={viewWizard.open} startIcon={<AddCircle />} variant="outlined">
            {t('pages.shows.detail.actions.create_view_button')}
          </Button>
        )}
        <Tooltip title={show.canDestroy.reasons?.fullMessages.join(', ')}>
          <span>
            <DeleteButton disabled={!show.canDestroy.value} onClick={handleDelete} />
          </span>
        </Tooltip>
      </Box>
      <ConfirmDialog
        {...confirmProps}
        confirm={t('pages.shows.detail.actions.delete_show_confirm')}
        deleteConfirm
        prompt={prompt}
        title={t('pages.shows.detail.actions.delete_show_title')}
      />
      <CreateViewWizard {...viewWizard.props} show={show} setViewId={setViewId} />
    </>
  );
};
